import React, { type KeyboardEvent, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

import { RentalImportBatchSummary, useRentalImportBatchUsers } from '~/hooks/apis/vendors/useBulkUploadRentals';

import { formatDateSlashesWithClock, parseDayjs } from '~/services/TimeService';

const TABLE_MAX_HEIGHT = 260;
const BATCH_ID_COLUMN_WIDTH = 380;

export function selectVisibleBatches(
  batches: Array<RentalImportBatchSummary>,
  {
    showMineOnly,
    currentUserId,
    sortDirection,
  }: { showMineOnly: boolean; currentUserId?: string; sortDirection: 'asc' | 'desc' },
): Array<RentalImportBatchSummary> {
  const filtered = showMineOnly && currentUserId ? batches.filter((batch) => batch.user_id === currentUserId) : batches;
  return [...filtered].sort((a, b) => {
    const diff = parseDayjs(a.created_at).valueOf() - parseDayjs(b.created_at).valueOf();
    return sortDirection === 'asc' ? diff : -diff;
  });
}

function handleRowKeyDown(event: KeyboardEvent, onActivate: () => void) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onActivate();
  }
}

interface Props {
  batches: Array<RentalImportBatchSummary>;
  currentUserId?: string;
  isError?: boolean;
  isLoading: boolean;
  isUserLoading?: boolean;
  onSelectBatch: (batchId: string) => void;
}

export default function BatchHistoryTable({
  batches,
  currentUserId,
  isError,
  isLoading,
  isUserLoading,
  onSelectBatch,
}: Props) {
  const [showMineOnly, setShowMineOnly] = useState(true);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const { data: usersById } = useRentalImportBatchUsers(batches.map((batch) => batch.user_id));

  const isPending = isLoading || isUserLoading;

  const visibleBatches = useMemo(
    () => selectVisibleBatches(batches, { showMineOnly, currentUserId, sortDirection }),
    [batches, showMineOnly, currentUserId, sortDirection],
  );

  if (isError) {
    return <Alert severity="error">Failed to load batch history</Alert>;
  }

  if (!isPending && batches.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2">
        No batches uploaded yet
      </Typography>
    );
  }

  return (
    <Box>
      {(isPending || currentUserId) && (
        <FormControlLabel
          control={
            <Checkbox
              checked={showMineOnly}
              disabled={isPending}
              onChange={(e) => setShowMineOnly(e.target.checked)}
              size="small"
            />
          }
          label="Show only mine"
          sx={{ mb: 0.5 }}
        />
      )}
      {isPending ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={20} />
        </Box>
      ) : visibleBatches.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {showMineOnly
            ? 'No batches uploaded by you yet — turn off "Show only mine" to see all'
            : 'No batches to show'}
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: TABLE_MAX_HEIGHT, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: BATCH_ID_COLUMN_WIDTH }}>Batch ID</TableCell>
                <TableCell>Properties</TableCell>
                <TableCell sortDirection={sortDirection}>
                  <TableSortLabel
                    active
                    direction={sortDirection}
                    onClick={() => setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))}
                  >
                    Created At
                  </TableSortLabel>
                </TableCell>
                <TableCell>Created By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleBatches.map((batch) => {
                const user = usersById?.get(batch.user_id);
                return (
                  <TableRow
                    hover
                    key={batch.batch_id}
                    role="button"
                    sx={{
                      cursor: 'pointer',
                      '&:focus-visible': {
                        outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: -2,
                        bgcolor: 'action.focus',
                      },
                    }}
                    tabIndex={0}
                    aria-label={`Open batch ${batch.batch_id}`}
                    onClick={() => onSelectBatch(batch.batch_id)}
                    onKeyDown={(event) => handleRowKeyDown(event, () => onSelectBatch(batch.batch_id))}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', width: BATCH_ID_COLUMN_WIDTH }}>
                      {batch.batch_id}
                    </TableCell>
                    <TableCell>{batch.total_rows}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {batch.created_at ? formatDateSlashesWithClock(batch.created_at) : '-'}
                    </TableCell>
                    <TableCell>{user?.full_name ?? user?.email ?? 'Unknown user'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
